import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { ConfidenceBadge } from '@/components/procurement/ConfidenceBadge';
import { CustomerAllocationBadge } from '@/components/procurement/CustomerAllocationBadge';
import { WhyPanel } from '@/components/procurement/WhyPanel';
import { trackEvent } from '@/utils/telemetry';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  XCircle, 
  CheckCircle, 
  Bell, 
  Package,
  ShoppingCart,
  Truck,
  Clock,
  ArrowRight,
  RefreshCw,
  Filter,
  Eye,
  Plus,
  Minus,
  TrendingDown,
  Calendar,
  AlertCircle,
  Info,
  Search,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pagination component
const Pagination = ({ currentPage, totalPages, totalItems, pageSize, onPageChange }) => {
  if (totalPages <= 1) return null;
  
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  
  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };
  
  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t" data-testid="pagination">
      <p className="text-sm text-muted-foreground">
        A mostrar {startItem}-{endItem} de {totalItems} itens
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          data-testid="pagination-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        {getPageNumbers().map(pageNum => (
          <Button
            key={pageNum}
            variant={pageNum === currentPage ? 'default' : 'outline'}
            size="sm"
            onClick={() => onPageChange(pageNum)}
            className="min-w-[36px]"
            data-testid={`pagination-page-${pageNum}`}
          >
            {pageNum}
          </Button>
        ))}
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          data-testid="pagination-next"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const StockAlerts = () => {
  // State
  const [alerts, setAlerts] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [scopeSummary, setScopeSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alerts');
  
  // Filters
  const [filterRisk, setFilterRisk] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');  // all | out_of_stock | low_stock
  const [filterPendingAction, setFilterPendingAction] = useState(false);
  const [sortAlerts, setSortAlerts] = useState('severity');  // severity | sku | name | stock | recent
  const [sortPredictions, setSortPredictions] = useState('risk');  // risk | sku | name | days
  const [searchQuery, setSearchQuery] = useState('');
  const [showVirtualStock, setShowVirtualStock] = useState(true);
  
  // Add to cart modal
  const [addToCartModal, setAddToCartModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [cartQuantity, setCartQuantity] = useState(0);
  const [cartOrderType, setCartOrderType] = useState('stock_replenishment');
  const [submitting, setSubmitting] = useState(false);
  const [pendingOrdersInfo, setPendingOrdersInfo] = useState(null);
  
  // Pagination
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [alertsPage, setAlertsPage] = useState(1);
  const [predictionsPage, setPredictionsPage] = useState(1);
  const PAGE_SIZE = 50;

  useEffect(() => {
    fetchAlerts();
    fetchPredictions();
    fetchScope();
  }, []);

  const fetchScope = async () => {
    try {
      const res = await axios.get(`${API}/procurement-config/scope-summary`);
      setScopeSummary(res.data);
    } catch {
      // silent
    }
  };

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      // Fetch all alerts for filtering, pagination done client-side after filtering
      const alertsRes = await axios.get(`${API}/alerts?page=1&page_size=500`);
      setAlerts(alertsRes.data.items || []);
      setPagination({
        page: alertsRes.data.page || 1,
        totalPages: alertsRes.data.total_pages || 1,
        total: alertsRes.data.total || 0
      });
      setAlertsPage(1); // Reset to first page on new data
    } catch (error) {
      toast.error('Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  };

  const fetchPredictions = async () => {
    try {
      const predictionsRes = await axios.get(`${API}/predictions?limit=500`);
      setPredictions(predictionsRes.data || []);
      setPredictionsPage(1); // Reset to first page on new data
    } catch (error) {
      console.error('Error loading predictions:', error);
      // Don't show error toast - predictions are optional enhancement
    }
  };

  const fetchData = async () => {
    await fetchAlerts();
    await fetchPredictions();
  };

  // Recalculate alerts in backend and then refresh data
  const [recalculating, setRecalculating] = useState(false);
  
  const recalculateAlerts = async () => {
    setRecalculating(true);
    try {
      await axios.post(`${API}/alerts/recalculate`);
      toast.success('Recálculo de alertas iniciado. A atualizar dados...');
      // Wait a moment for backend to process, then refresh
      await new Promise(resolve => setTimeout(resolve, 1500));
      await fetchData();
      toast.success('Alertas atualizados com sucesso!');
    } catch (error) {
      console.error('Error recalculating alerts:', error);
      toast.error(error.response?.data?.detail || 'Erro ao recalcular alertas');
    } finally {
      setRecalculating(false);
    }
  };

  // Combined stats
  const stats = useMemo(() => {
    const alertStats = {
      total: alerts.length,
      critical: alerts.filter(a => a.alert_type === 'out_of_stock').length,
      low: alerts.filter(a => a.alert_type === 'low_stock').length,
      pending: alerts.filter(a => a.status === 'pending').length,
    };
    
    const predictionStats = {
      total: predictions.length,
      critical: predictions.filter(p => (showVirtualStock ? p.virtual_risk_level : p.risk_level) === 'critical').length,
      high: predictions.filter(p => (showVirtualStock ? p.virtual_risk_level : p.risk_level) === 'high').length,
      medium: predictions.filter(p => (showVirtualStock ? p.virtual_risk_level : p.risk_level) === 'medium').length,
      low: predictions.filter(p => (showVirtualStock ? p.virtual_risk_level : p.risk_level) === 'low').length,
      withPendingAction: predictions.filter(p => p.has_pending_action).length,
    };
    
    return { alerts: alertStats, predictions: predictionStats };
  }, [alerts, predictions, showVirtualStock]);

  // Filtered alerts
  const filteredAlerts = useMemo(() => {
    const riskOrder = { out_of_stock: 0, low_stock: 1 };
    const sortFns = {
      severity: (a, b) => (riskOrder[a.alert_type] ?? 9) - (riskOrder[b.alert_type] ?? 9) || (a.product_sku || '').localeCompare(b.product_sku || ''),
      sku: (a, b) => (a.product_sku || '').localeCompare(b.product_sku || ''),
      name: (a, b) => (a.product_name || '').localeCompare(b.product_name || '', 'pt'),
      stock: (a, b) => (a.current_stock || 0) - (b.current_stock || 0),
      recent: (a, b) => (b.created_at || '').localeCompare(a.created_at || ''),
    };
    return alerts.filter(alert => {
      if (filterStatus !== 'all' && alert.status !== filterStatus) return false;
      if (filterType !== 'all' && alert.alert_type !== filterType) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        // Alert schema uses 'sku' not 'product_sku'
        return alert.product_name?.toLowerCase().includes(query) || 
               alert.sku?.toLowerCase().includes(query) ||
               alert.product_sku?.toLowerCase().includes(query);
      }
      return true;
    }).sort(sortFns[sortAlerts] || sortFns.severity);
  }, [alerts, filterStatus, filterType, searchQuery, sortAlerts]);

  // Filtered predictions
  const filteredPredictions = useMemo(() => {
    const riskOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const riskOf = (p) => (showVirtualStock ? p.virtual_risk_level : p.risk_level);
    const sortFns = {
      risk: (a, b) => (riskOrder[riskOf(a)] ?? 9) - (riskOrder[riskOf(b)] ?? 9) || (a.sku || '').localeCompare(b.sku || ''),
      sku: (a, b) => (a.sku || '').localeCompare(b.sku || ''),
      name: (a, b) => (a.product_name || '').localeCompare(b.product_name || '', 'pt'),
      days: (a, b) => (a.days_until_stockout ?? 9999) - (b.days_until_stockout ?? 9999),
    };
    return predictions.filter(p => {
      const riskLevel = riskOf(p);
      if (filterRisk !== 'all' && riskLevel !== filterRisk) return false;
      if (filterPendingAction && !p.has_pending_action) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return p.product_name?.toLowerCase().includes(query) || 
               p.sku?.toLowerCase().includes(query);
      }
      return true;
    }).sort(sortFns[sortPredictions] || sortFns.risk);
  }, [predictions, filterRisk, filterPendingAction, searchQuery, showVirtualStock, sortPredictions]);

  // Paginated alerts (client-side pagination after filtering)
  const paginatedAlerts = useMemo(() => {
    const startIndex = (alertsPage - 1) * PAGE_SIZE;
    return filteredAlerts.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredAlerts, alertsPage]);

  const alertsTotalPages = Math.ceil(filteredAlerts.length / PAGE_SIZE) || 1;

  // Paginated predictions (client-side pagination after filtering)
  const paginatedPredictions = useMemo(() => {
    const startIndex = (predictionsPage - 1) * PAGE_SIZE;
    return filteredPredictions.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredPredictions, predictionsPage]);

  const predictionsTotalPages = Math.ceil(filteredPredictions.length / PAGE_SIZE) || 1;

  // Reset page when filters change
  useEffect(() => {
    setAlertsPage(1);
  }, [filterStatus, filterType, searchQuery, sortAlerts]);

  useEffect(() => {
    setPredictionsPage(1);
  }, [filterRisk, filterPendingAction, searchQuery, showVirtualStock, sortPredictions]);

  // Cards do topo → filtram a lista respectiva ao clicar
  const applyCardFilter = (card) => {
    setSearchQuery('');
    setFilterPendingAction(false);
    if (card === 'critical') {
      setActiveTab('alerts'); setFilterType('out_of_stock'); setFilterStatus('all');
    } else if (card === 'low') {
      setActiveTab('alerts'); setFilterType('low_stock'); setFilterStatus('all');
    } else if (card === 'high_risk') {
      setActiveTab('predictions'); setFilterRisk('high');
    } else if (card === 'pending_action') {
      setActiveTab('predictions'); setFilterRisk('all'); setFilterPendingAction(true);
    } else {
      setActiveTab('alerts'); setFilterType('all'); setFilterStatus('all');
    }
  };

  // Open add to cart modal
  const openAddToCart = async (item, isAlert = true) => {
    trackEvent('suggestion_action', {
      action: 'open_add_to_cart',
      sku: item.sku,
      source: isAlert ? 'alert' : 'prediction',
      risk: item.risk_level || item.virtual_risk_level,
      suggested_qty: item.suggested_order_quantity,
      client_allocation_qty: item.client_allocation_qty || 0,
    });
    const productId = isAlert ? item.product_id : item.product_id;
    const sku = isAlert ? item.product_sku : item.sku;
    
    setSelectedItem({
      ...item,
      isAlert,
      sku: sku,
      name: isAlert ? item.product_name : item.product_name,
      suggestedQty: isAlert ? (item.reorder_quantity || 10) : (item.suggested_order_quantity || 10)
    });
    setCartQuantity(isAlert ? (item.reorder_quantity || 10) : (item.suggested_order_quantity || 10));
    setCartOrderType('stock_replenishment');
    setPendingOrdersInfo(null);
    
    // Check for pending orders
    try {
      const response = await axios.get(`${API}/purchase-orders/check-pending/${productId}`);
      if (response.data.pending_quantity > 0) {
        setPendingOrdersInfo(response.data);
      }
    } catch (error) {
      console.error('Error checking pending orders:', error);
    }
    
    setAddToCartModal(true);
  };

  // Add to cart
  const handleAddToCart = async () => {
    if (!selectedItem || cartQuantity <= 0) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/shopping-cart`, {
        product_sku: selectedItem.sku,
        quantity: cartQuantity,
        item_type: cartOrderType === 'stock_replenishment' ? 'reposition' : 'client_order'
      });
      
      toast.success(`${selectedItem.sku} adicionado ao carrinho`);
      setAddToCartModal(false);
      
      // Mark alert as in_progress if it's an alert
      if (selectedItem.isAlert && selectedItem.id) {
        try {
          await axios.patch(`${API}/alerts/${selectedItem.id}/read`);
        } catch {
          // Silently ignore read-marking errors
        }
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar ao carrinho');
    } finally {
      setSubmitting(false);
    }
  };

  // Risk colors
  // Sessão 33 (Phase α): calmer palette — only the badge carries color.
  // Card background stays neutral so the list feels calm; the operator's
  // eye is drawn to the badge, not to stripes of color.
  const getRiskStyle = (risk) => {
    switch (risk) {
      case 'critical':
        return { bg: '', border: '', text: 'text-red-700', badge: 'bg-red-50 text-red-700 border border-red-200' };
      case 'high':
        return { bg: '', border: '', text: 'text-orange-700', badge: 'bg-orange-50 text-orange-700 border border-orange-200' };
      case 'medium':
        return { bg: '', border: '', text: 'text-amber-700', badge: 'bg-amber-50 text-amber-700 border border-amber-200' };
      default:
        return { bg: '', border: '', text: 'text-emerald-700', badge: 'bg-emerald-50 text-emerald-700 border border-emerald-200' };
    }
  };

  const getRiskLabel = (risk) => {
    switch (risk) {
      case 'critical': return 'Crítico';
      case 'high': return 'Alto';
      case 'medium': return 'Médio';
      default: return 'Baixo';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'out_of_stock': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'low_stock': return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      default: return <Bell className="h-5 w-5 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <div className="animate-pulse">
              <div className="h-8 w-64 bg-slate-200 rounded mb-2"></div>
              <div className="h-4 w-96 bg-slate-100 rounded"></div>
            </div>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 flex items-center justify-center min-h-[400px]">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Bell className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                Alertas & Previsões
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Monitorização de stock e previsão de ruturas
              </p>
            </div>
            <Button
              variant="outline"
              onClick={recalculateAlerts}
              disabled={loading || recalculating}
              data-testid="recalculate-alerts-btn"
              className="shrink-0"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
              {recalculating ? 'A recalcular...' : 'Atualizar'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">

      {/* Procurement scope status — Phase 2 honesty banner */}
      {scopeSummary && scopeSummary.products_in_scope === 0 && (
        <Card className="border border-amber-300 bg-amber-50" data-testid="scope-empty-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Cockpit vazio — nenhum SKU em scope de procurement</p>
              <p className="mt-0.5">
                Os alertas e previsões só aparecem para produtos com AI tracking activo
                + fornecedor com Procurement Intelligence activa. Vai a{' '}
                <Link to="/admin/procurement" className="underline font-medium">
                  Procurement AI
                </Link>{' '}
                para activar fornecedores e SKUs.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {scopeSummary && scopeSummary.products_in_scope > 0 && (
        <div className="text-xs text-slate-500" data-testid="scope-status-line">
          A monitorizar {scopeSummary.products_in_scope.toLocaleString('pt-PT')} SKUs
          ({scopeSummary.suppliers_pi_enabled}/{scopeSummary.suppliers_total} fornecedores activos)
        </div>
      )}

      {/* Summary Stats — inline, sóbrio, profissional */}
      <div className="flex flex-wrap items-center gap-6 text-sm border-b border-slate-200 pb-4">
        <button 
          onClick={() => applyCardFilter('critical')} 
          className={`flex items-center gap-2 hover:text-red-600 transition-colors ${activeTab === 'alerts' && filterType === 'out_of_stock' ? 'text-red-600 font-semibold' : 'text-slate-600'}`}
          data-testid="card-filter-critical"
        >
          <span className="text-xl font-bold tabular-nums">{stats.alerts.critical}</span>
          <span>Sem Stock</span>
        </button>
        <button 
          onClick={() => applyCardFilter('low')} 
          className={`flex items-center gap-2 hover:text-amber-600 transition-colors ${activeTab === 'alerts' && filterType === 'low_stock' ? 'text-amber-600 font-semibold' : 'text-slate-600'}`}
          data-testid="card-filter-low"
        >
          <span className="text-xl font-bold tabular-nums">{stats.alerts.low}</span>
          <span>Stock Baixo</span>
        </button>
        <button 
          onClick={() => applyCardFilter('high_risk')} 
          className={`flex items-center gap-2 hover:text-orange-600 transition-colors ${activeTab === 'predictions' && filterRisk === 'high' ? 'text-orange-600 font-semibold' : 'text-slate-600'}`}
          data-testid="card-filter-high-risk"
        >
          <span className="text-xl font-bold tabular-nums">{stats.predictions.high}</span>
          <span>Risco Alto</span>
        </button>
        <button 
          onClick={() => applyCardFilter('pending_action')} 
          className={`flex items-center gap-2 hover:text-blue-600 transition-colors ${activeTab === 'predictions' && filterPendingAction ? 'text-blue-600 font-semibold' : 'text-slate-600'}`}
          data-testid="card-filter-pending-action"
        >
          <span className="text-xl font-bold tabular-nums">{stats.predictions.withPendingAction}</span>
          <span>Ação Pendente</span>
        </button>
        <button 
          onClick={() => applyCardFilter('all')} 
          className={`flex items-center gap-2 hover:text-slate-900 transition-colors ${!filterType && !filterRisk && !filterPendingAction ? 'text-slate-900 font-semibold' : 'text-slate-500'}`}
          data-testid="card-filter-all"
        >
          <span className="text-xl font-bold tabular-nums">{stats.alerts.total}</span>
          <span>Total</span>
        </button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar por SKU ou nome..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="alerts-search-input"
                />
              </div>
            </div>
            {activeTab === 'alerts' && (
              <>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[160px]" data-testid="alerts-type-filter">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    <SelectItem value="out_of_stock">Sem Stock</SelectItem>
                    <SelectItem value="low_stock">Stock Baixo</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[160px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pending">Pendentes</SelectItem>
                    <SelectItem value="in_progress">Em Progresso</SelectItem>
                    <SelectItem value="resolved">Resolvidos</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortAlerts} onValueChange={setSortAlerts}>
                  <SelectTrigger className="w-[190px]" data-testid="alerts-sort-select">
                    <SelectValue placeholder="Ordenar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="severity">Severidade</SelectItem>
                    <SelectItem value="sku">SKU (A-Z)</SelectItem>
                    <SelectItem value="name">Nome (A-Z)</SelectItem>
                    <SelectItem value="stock">Stock (menor primeiro)</SelectItem>
                    <SelectItem value="recent">Mais recentes</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            {activeTab === 'predictions' && (
              <>
                <Select value={filterRisk} onValueChange={(v) => { setFilterRisk(v); trackEvent('filter_changed', { page: 'alerts', filter: 'risk', value: v }); }}>
                  <SelectTrigger className="w-[160px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Risco" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="critical">Crítico</SelectItem>
                    <SelectItem value="high">Alto</SelectItem>
                    <SelectItem value="medium">Médio</SelectItem>
                    <SelectItem value="low">Baixo</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortPredictions} onValueChange={setSortPredictions}>
                  <SelectTrigger className="w-[190px]" data-testid="predictions-sort-select">
                    <SelectValue placeholder="Ordenar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="risk">Risco</SelectItem>
                    <SelectItem value="sku">SKU (A-Z)</SelectItem>
                    <SelectItem value="name">Nome (A-Z)</SelectItem>
                    <SelectItem value="days">Dias até rutura</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); trackEvent('tab_opened', { page: 'alerts', tab: v }); }}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="alerts" className="gap-2">
            <Bell className="h-4 w-4" />
            Alertas ({filteredAlerts.length})
          </TabsTrigger>
          <TabsTrigger value="predictions" className="gap-2">
            <TrendingDown className="h-4 w-4" />
            Previsões ({filteredPredictions.length})
          </TabsTrigger>
        </TabsList>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="mt-4">
          {filteredAlerts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-semibold">Tudo sob controlo!</h3>
                <p className="text-muted-foreground">Não há alertas de stock pendentes.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid gap-3">
                {paginatedAlerts.map(alert => (
                  <Card 
                    key={alert.id} 
                    className={`${alert.alert_type === 'out_of_stock' ? 'border-red-200 bg-red-50/50' : 'border-orange-200 bg-orange-50/50'}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          {getAlertIcon(alert.alert_type)}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-sm font-medium">{alert.product_sku}</span>
                              <Badge variant={alert.alert_type === 'out_of_stock' ? 'destructive' : 'secondary'}>
                                {alert.alert_type === 'out_of_stock' ? 'Sem Stock' : 'Stock Baixo'}
                              </Badge>
                              {alert.status === 'in_progress' && (
                                <Badge variant="outline" className="text-blue-600">Em Progresso</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">{alert.product_name}</p>
                            <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                              <span>Stock: <strong className={alert.current_stock === 0 ? 'text-red-600' : ''}>{alert.current_stock}</strong></span>
                              <span>Mínimo: {alert.reorder_point ?? '—'}</span>
                              {alert.supplier && <span>Fornecedor: {alert.supplier}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            asChild
                          >
                            <Link to={`/inventory/${alert.product_id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openAddToCart(alert, true)}
                          >
                            <ShoppingCart className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Carrinho</span>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Pagination
                currentPage={alertsPage}
                totalPages={alertsTotalPages}
                totalItems={filteredAlerts.length}
                pageSize={PAGE_SIZE}
                onPageChange={setAlertsPage}
              />
            </>
          )}
        </TabsContent>

        {/* Predictions Tab */}
        <TabsContent value="predictions" className="mt-4">
          {/* Toggle Virtual Stock */}
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant={showVirtualStock ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowVirtualStock(true)}
            >
              Stock Virtual
            </Button>
            <Button
              variant={!showVirtualStock ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowVirtualStock(false)}
            >
              Stock Físico
            </Button>
            <span className="text-xs text-muted-foreground">
              {showVirtualStock ? '(Inclui encomendas pendentes)' : '(Apenas stock actual)'}
            </span>
          </div>

          {filteredPredictions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-semibold">Sem previsões de risco!</h3>
                <p className="text-muted-foreground">Todos os produtos têm stock adequado.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid gap-3">
                {paginatedPredictions.map(prediction => {
                  const riskLevel = showVirtualStock ? prediction.virtual_risk_level : prediction.risk_level;
                  const style = getRiskStyle(riskLevel);
                  const daysUntil = showVirtualStock ? prediction.virtual_days_until_stockout : prediction.days_until_stockout;
                  
                  return (
                    <Card 
                      key={prediction.product_id} 
                      className={`${style.bg} ${style.border} border`}
                    >
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`p-2 rounded-lg ${style.badge}`}>
                              <TrendingDown className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-sm font-medium">{prediction.sku}</span>
                                <Badge className={style.badge}>
                                  {getRiskLabel(riskLevel)}
                                </Badge>
                                {prediction.has_pending_action && (
                                  <Badge variant="outline" className="text-blue-600">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Pendente
                                  </Badge>
                                )}
                                {/* Sessão 32 — QW1 + QW4: customer allocations + confidence */}
                                {prediction.client_allocation_qty > 0 && (
                                  <CustomerAllocationBadge
                                    allocations={prediction.client_allocations}
                                    totalQty={prediction.client_allocation_qty}
                                    testid={prediction.sku}
                                  />
                                )}
                                {prediction.confidence && (
                                  <ConfidenceBadge
                                    confidence={prediction.confidence}
                                    size="xs"
                                    testid={prediction.sku}
                                  />
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground truncate">{prediction.product_name}</p>
                              <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground flex-wrap">
                                <span>Stock: <strong>{showVirtualStock ? prediction.virtual_stock : prediction.current_stock}</strong></span>
                                <span>Vendas/dia: {prediction.average_daily_sales?.toFixed(1)}</span>
                                {daysUntil !== null && (
                                  <span className={daysUntil <= 7 ? 'text-red-600 font-medium' : ''}>
                                    <Calendar className="h-3 w-3 inline mr-1" />
                                    {daysUntil <= 0 ? 'Em rutura!' : `${daysUntil} dias`}
                                  </span>
                                )}
                                {prediction.pending_orders > 0 && (
                                  <span className="text-blue-600">
                                    <Truck className="h-3 w-3 inline mr-1" />
                                    {prediction.pending_orders} pendente
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {/* Sessão 32 — QW3: Why? panel */}
                            <WhyPanel data={prediction} compact testid={prediction.sku} />
                            <Button
                              size="sm"
                              variant="outline"
                              asChild
                            >
                              <Link to={`/inventory/${prediction.product_id}`}>
                                <Eye className="h-4 w-4" />
                              </Link>
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => openAddToCart(prediction, false)}
                            >
                              <ShoppingCart className="h-4 w-4 mr-1" />
                              <span className="hidden sm:inline">Carrinho</span>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <Pagination
                currentPage={predictionsPage}
                totalPages={predictionsTotalPages}
                totalItems={filteredPredictions.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPredictionsPage}
              />
            </>
          )}
        </TabsContent>
      </Tabs>

      </div>

      {/* Add to Cart Modal */}
      <Dialog open={addToCartModal} onOpenChange={setAddToCartModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar ao Carrinho</DialogTitle>
            <DialogDescription>
              {selectedItem?.sku} - {selectedItem?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Pending Orders Warning */}
            {pendingOrdersInfo && pendingOrdersInfo.pending_quantity > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Encomenda pendente detectada</p>
                    <p className="text-amber-700">
                      Já existe uma encomenda de <strong>{pendingOrdersInfo.pending_quantity}</strong> unidades
                      {pendingOrdersInfo.po_numbers?.length > 0 && (
                        <> (PO: {pendingOrdersInfo.po_numbers.join(', ')})</>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Quantity */}
            <div>
              <Label>Quantidade</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCartQuantity(Math.max(1, cartQuantity - 1))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  min={1}
                  value={cartQuantity}
                  onChange={(e) => setCartQuantity(parseInt(e.target.value) || 1)}
                  className="w-24 text-center"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCartQuantity(cartQuantity + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  (Sugerido: {selectedItem?.suggestedQty})
                </span>
              </div>
            </div>
            
            {/* Order Type */}
            <div>
              <Label>Tipo de Encomenda</Label>
              <Select value={cartOrderType} onValueChange={setCartOrderType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stock_replenishment">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Reposição de Stock
                    </div>
                  </SelectItem>
                  <SelectItem value="client_order">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4" />
                      Pedido de Cliente
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddToCartModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddToCart} disabled={submitting}>
              {submitting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ShoppingCart className="h-4 w-4 mr-2" />
              )}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockAlerts;
